import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, X, Loader2 } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { ZoomableImage } from "@/components/native/ZoomableImage";
import { useFullscreenSwipeDismiss } from "@/hooks/useFullscreenSwipeDismiss";
import { useNativeMediaViewerOpen } from "@/hooks/useNativeMediaViewerOpen";
import { nativeMediaViewerZ } from "@/lib/native/nativeZIndex";
import {
  useMediaViewerGestureLock,
  useMediaViewerZoomState,
} from "@/lib/native/mediaViewerGestureLock";
import { cn } from "@/lib/utils";
import GalleryIndicators from "./GalleryIndicators";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { isVideoMedia } from "@/lib/isVideoMedia";

const MIN_SWIPE_DISTANCE = 50;

export interface FullscreenGalleryProps {
  images: string[];
  /** Parallel MIME hints when URLs lack extensions (e.g. presigned video). */
  mediaTypes?: (string | null | undefined)[];
  imageAlts?: string[];
  title: string;
  currentIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (direction: "prev" | "next") => void;
  onSelect: (index: number) => void;
}

/**
 * Portaled fullscreen image viewer — same interaction model as CarImageGallery fullscreen.
 */
const FullscreenGallery = ({
  images,
  mediaTypes,
  imageAlts,
  title,
  currentIndex,
  open,
  onOpenChange,
  onNavigate,
  onSelect,
}: FullscreenGalleryProps) => {
  const isMobile = useIsMobile();
  const closeGallery = useCallback(() => onOpenChange(false), [onOpenChange]);

  const imageGestureLockRef = useMediaViewerGestureLock();
  const { imageZoomed, imageZoomedRef, onZoomedChange, resetZoomed } =
    useMediaViewerZoomState();

  const [isLoading, setIsLoading] = useState(true);
  const [fullscreenCarouselApi, setFullscreenCarouselApi] =
    useState<CarouselApi | null>(null);

  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const mouseMoved = useRef(false);

  const fullscreenCarouselOpts = useMemo(
    () => ({
      align: "center" as const,
      loop: false,
      dragFree: false,
      watchDrag: () => !imageZoomedRef.current,
    }),
    [imageZoomedRef],
  );

  const swipeDismiss = useFullscreenSwipeDismiss(closeGallery, {
    gestureLockRef: imageGestureLockRef,
  });

  const fullscreenSwipeHandlers = useMemo(
    () => ({
      onTouchStart: swipeDismiss.onTouchStart,
      onTouchMove: swipeDismiss.onTouchMove,
      onTouchEnd: swipeDismiss.onTouchEnd,
    }),
    [
      swipeDismiss.onTouchStart,
      swipeDismiss.onTouchMove,
      swipeDismiss.onTouchEnd,
    ],
  );

  useEffect(() => {
    if (!open) resetZoomed();
  }, [open, resetZoomed]);

  useEffect(() => {
    if (images.length <= 1) setIsLoading(true);
  }, [currentIndex, images.length]);

  useEffect(() => {
    if (!fullscreenCarouselApi || !open) return;
    fullscreenCarouselApi.reInit();
  }, [fullscreenCarouselApi, open, imageZoomed]);

  useNativeMediaViewerOpen(open);

  useEffect(() => {
    if (!fullscreenCarouselApi || !open || images.length <= 1) return;
    const onSelectSlide = () => {
      const idx = fullscreenCarouselApi.selectedScrollSnap();
      onSelect(idx);
    };
    fullscreenCarouselApi.on("select", onSelectSlide);
    return () => {
      fullscreenCarouselApi.off("select", onSelectSlide);
    };
  }, [fullscreenCarouselApi, open, images.length, onSelect]);

  useEffect(() => {
    if (!open || !fullscreenCarouselApi || images.length <= 1) return;
    if (fullscreenCarouselApi.selectedScrollSnap() !== currentIndex) {
      fullscreenCarouselApi.scrollTo(currentIndex);
    }
  }, [open, fullscreenCarouselApi, currentIndex, images.length]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeGallery();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [open, closeGallery]);

  useEffect(() => {
    if (!open || isMobile) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (imageZoomedRef.current) return;
      if (e.key === "ArrowLeft" && images.length > 1) {
        e.preventDefault();
        onNavigate("prev");
      } else if (e.key === "ArrowRight" && images.length > 1) {
        e.preventDefault();
        onNavigate("next");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isMobile, images.length, onNavigate, imageZoomedRef]);

  const onSingleTouchStart = (e: React.TouchEvent) => {
    touchEndX.current = null;
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const onSingleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const onSingleTouchEnd = () => {
    if (imageZoomedRef.current) return;
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > MIN_SWIPE_DISTANCE && images.length > 1) {
      onNavigate("next");
    } else if (distance < -MIN_SWIPE_DISTANCE && images.length > 1) {
      onNavigate("prev");
    }
  };

  const onMouseDown = (e: React.MouseEvent) => {
    mouseMoved.current = false;
    touchEndX.current = null;
    touchStartX.current = e.clientX;
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (touchStartX.current !== null) {
      const moved = Math.abs(e.clientX - touchStartX.current) > 5;
      if (moved) mouseMoved.current = true;
      touchEndX.current = e.clientX;
    }
  };

  const onMouseUp = () => {
    if (imageZoomedRef.current) {
      touchStartX.current = null;
      touchEndX.current = null;
      mouseMoved.current = false;
      return;
    }
    if (!mouseMoved.current) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > MIN_SWIPE_DISTANCE && images.length > 1) {
      onNavigate("next");
    } else if (distance < -MIN_SWIPE_DISTANCE && images.length > 1) {
      onNavigate("prev");
    }
    touchStartX.current = null;
    touchEndX.current = null;
    mouseMoved.current = false;
  };

  const getAlt = (index: number) =>
    imageAlts?.[index] ?? `${title} ${index + 1}`;

  if (!open || typeof document === "undefined") return null;

  const singleImageMode = images.length <= 1;

  const fullscreenContent = (
    <div
      className={cn(
        "native-media-viewer-layer fixed inset-0 h-dvh w-dvh bg-black",
        nativeMediaViewerZ,
      )}
      role="dialog"
      aria-modal="true"
      aria-label={`${title} fullscreen`}
    >
      <div
        className="relative flex h-full w-full items-center justify-center overflow-hidden bg-black"
        onTouchStart={(e) => {
          if (imageZoomedRef.current) return;
          fullscreenSwipeHandlers.onTouchStart?.(e);
          if (singleImageMode) onSingleTouchStart(e);
        }}
        onTouchMove={(e) => {
          if (imageZoomedRef.current) return;
          fullscreenSwipeHandlers.onTouchMove?.(e);
          if (singleImageMode) onSingleTouchMove(e);
        }}
        onTouchEnd={(e) => {
          if (imageZoomedRef.current) return;
          fullscreenSwipeHandlers.onTouchEnd?.(e);
          if (singleImageMode) onSingleTouchEnd();
        }}
        onMouseDown={singleImageMode && !imageZoomed ? onMouseDown : undefined}
        onMouseMove={singleImageMode && !imageZoomed ? onMouseMove : undefined}
        onMouseUp={singleImageMode && !imageZoomed ? onMouseUp : undefined}
        onMouseLeave={singleImageMode && !imageZoomed ? onMouseUp : undefined}
      >
        <button
          type="button"
          onClick={closeGallery}
          className={`absolute ${
            isMobile ? "top-12 right-2" : "top-2 right-2"
          } z-[9999] bg-black/50 hover:bg-black/70 ${
            isMobile ? "p-3" : "p-2"
          } rounded-full transition-colors touch-manipulation`}
          aria-label="Close fullscreen"
        >
          <X className={`${isMobile ? "h-7 w-7" : "h-6 w-6"} text-white`} />
        </button>

        {singleImageMode && isLoading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black">
            <Loader2 className="h-12 w-12 animate-spin text-white" />
          </div>
        )}

        {!singleImageMode ? (
          <Carousel
            setApi={setFullscreenCarouselApi}
            opts={fullscreenCarouselOpts}
            className="absolute inset-0 h-full w-full [&_.overflow-hidden]:!h-full"
          >
            <CarouselContent className="ml-0 h-full pl-0">
              {images.map((url, index) => (
                <CarouselItem
                  key={`fs-${index}`}
                  className="basis-full flex-shrink-0 flex-grow-0 pl-0"
                >
                  <div className="flex h-full w-full items-center justify-center p-4">
                    {isVideoMedia(mediaTypes?.[index], url) ? (
                      <video
                        src={url}
                        controls
                        playsInline
                        preload="metadata"
                        className="max-w-full max-h-full rounded shadow-lg"
                      />
                    ) : (
                      <ZoomableImage
                        src={url}
                        alt={getAlt(index)}
                        className="h-full w-full"
                        imgClassName="rounded shadow-lg"
                        draggable={false}
                        gestureLockRef={imageGestureLockRef}
                        onZoomedChange={onZoomedChange}
                      />
                    )}
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        ) : (
          <div className="flex h-full w-full items-center justify-center p-4">
            {isVideoMedia(
              mediaTypes?.[currentIndex] ?? mediaTypes?.[0],
              images[currentIndex] ?? images[0],
            ) ? (
              <video
                src={images[currentIndex] ?? images[0]}
                controls
                playsInline
                preload="metadata"
                className="max-w-full max-h-full rounded shadow-lg"
                onLoadedData={() => setIsLoading(false)}
                onError={() => setIsLoading(false)}
              />
            ) : (
              <ZoomableImage
                src={images[currentIndex] ?? images[0]}
                alt={getAlt(currentIndex)}
                className={cn(
                  "h-full w-full transition-opacity duration-200",
                  isLoading ? "opacity-0" : "opacity-100",
                )}
                imgClassName="rounded shadow-lg"
                draggable={false}
                gestureLockRef={imageGestureLockRef}
                onZoomedChange={onZoomedChange}
                onLoad={() => setIsLoading(false)}
                onError={() => setIsLoading(false)}
              />
            )}
          </div>
        )}

        {!singleImageMode && !imageZoomed && (
          <>
            <button
              type="button"
              onClick={() => onNavigate("prev")}
              disabled={currentIndex <= 0}
              className={`absolute left-2 top-1/2 z-[9999] -translate-y-1/2 rounded-full bg-black/50 transition-colors hover:bg-black/70 disabled:pointer-events-none disabled:opacity-40 ${
                isMobile ? "p-3" : "p-2"
              } touch-manipulation`}
              aria-label="Previous image"
            >
              <ChevronLeft
                className={`${isMobile ? "h-8 w-8" : "h-6 w-6"} text-white`}
              />
            </button>
            <button
              type="button"
              onClick={() => onNavigate("next")}
              disabled={currentIndex >= images.length - 1}
              className={`absolute right-2 top-1/2 z-[9999] -translate-y-1/2 rounded-full bg-black/50 transition-colors hover:bg-black/70 disabled:pointer-events-none disabled:opacity-40 ${
                isMobile ? "p-3" : "p-2"
              } touch-manipulation`}
              aria-label="Next image"
            >
              <ChevronRight
                className={`${isMobile ? "h-8 w-8" : "h-6 w-6"} text-white`}
              />
            </button>

            <GalleryIndicators
              count={images.length}
              currentIndex={currentIndex}
              onSelect={onSelect}
              size="large"
            />
          </>
        )}
      </div>
    </div>
  );

  return createPortal(fullscreenContent, document.body);
};

export default FullscreenGallery;
